import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import ValuationDialog from "./ValuationDialog";
import { Calculator, TrendingUp, TrendingDown, Minus } from "lucide-react";

function Sparkline({ points, isProfit }) {
  if (!points?.length || points.length < 2) {
    return (
      <div className="h-12 flex items-center justify-center text-[10px] text-zinc-600 font-mono">
        {points?.length === 1 ? "Run again later for a trend" : ""}
      </div>
    );
  }
  const w = 200;
  const h = 48;
  const values = points.map((p) => p.market_value || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = w / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = h - ((p.market_value - min) / range) * h;
    return `${x},${y}`;
  });
  const path = `M ${coords.join(" L ")}`;
  const stroke = isProfit ? "#10b981" : "#f43f5e";
  const fill = isProfit ? "rgba(16,185,129,0.15)" : "rgba(244,63,94,0.15)";
  const areaPath = `M 0,${h} L ${coords.join(" L ")} L ${w},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-12">
      <path d={areaPath} fill={fill} />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={w}
        cy={h - ((values[values.length - 1] - min) / range) * h}
        r={2.5}
        fill={stroke}
      />
    </svg>
  );
}

export default function ValuationCard() {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    try {
      const { data } = await api.get("/collection/valuation/history?days=30&limit=20");
      setHistory(data);
    } catch {
      setHistory({ snapshots: [], latest: null, change: null, days: 30 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const latest = history?.latest;
  const change = history?.change;
  const isProfit = (change?.pct ?? 0) >= 0;
  const hasData = !!latest;

  const lastUpdatedLabel = useMemo(() => {
    if (!latest?.created_at) return "";
    const d = new Date(latest.created_at);
    const hours = (Date.now() - d.getTime()) / 3600000;
    if (hours < 1) return "just now";
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }, [latest]);

  if (loading) {
    return <div data-testid="valuation-card-loading" className="bg-zinc-900/60 border border-white/5 rounded-xl p-5 h-36 animate-pulse" />;
  }

  return (
    <div data-testid="dashboard-valuation-card" className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-emerald-950/20 border border-emerald-500/15 rounded-xl p-5">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[10px] text-emerald-400/80 uppercase tracking-[0.25em] font-mono flex items-center gap-1">
            <Calculator className="w-3 h-3" /> Collection Value
          </p>
          {hasData ? (
            <p data-testid="valuation-card-value" className="font-heading text-3xl font-bold text-zinc-100 mt-1">
              ${Number(latest.market_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          ) : (
            <p className="font-heading text-lg text-zinc-400 mt-2">No valuation yet</p>
          )}
        </div>
        <ValuationDialog
          onRecalculated={fetchHistory}
          trigger={
            <button
              type="button"
              data-testid="valuation-card-cta"
              className="text-[11px] text-rose-400 hover:text-rose-300 underline underline-offset-2"
            >
              {hasData ? "Refresh" : "Calculate"}
            </button>
          }
        />
      </div>

      {hasData && (
        <>
          <div className="flex items-center gap-2 mb-2">
            {change && change.pct !== 0 ? (
              <span
                data-testid="valuation-card-change"
                className={`inline-flex items-center gap-1 text-xs font-mono ${isProfit ? "text-emerald-400" : "text-rose-400"}`}
              >
                {isProfit ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {isProfit ? "+" : ""}{change.pct.toFixed(1)}%
                <span className="text-zinc-500 font-sans text-[10px] ml-1">
                  ({isProfit ? "+" : "-"}${Math.abs(change.delta).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                </span>
              </span>
            ) : (
              <span data-testid="valuation-card-change" className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500">
                <Minus className="w-3.5 h-3.5" /> No change
              </span>
            )}
            <span className="text-[10px] text-zinc-600 ml-auto font-mono">{history.days}d · updated {lastUpdatedLabel}</span>
          </div>
          <Sparkline points={history.snapshots} isProfit={isProfit} />
        </>
      )}

      {!hasData && (
        <p className="text-xs text-zinc-500 mt-2">
          Click <span className="text-rose-400">Calculate</span> to generate your first collection valuation snapshot.
        </p>
      )}
    </div>
  );
}
